import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { inventoryRevisionStatusLabel } from "../shared/inventoryRevisionEnums";
import {
  fetchInventoryRevisionDetail,
  selectRevisionById,
  selectRevisionErrorById,
  selectRevisionStatusById,
  selectUpdateRevisionErrorById,
  selectUpdateRevisionStatusById,
  updateInventoryRevision,
} from "../features/inventoryRevisions/inventoryRevisionsSlice";
import "./WarehouseRevisionEditPage.css";

export default function WarehouseRevisionEditPage() {
  const { revisionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const revision = useSelector(selectRevisionById(revisionId));
  const loadStatus = useSelector(selectRevisionStatusById(revisionId));
  const loadError = useSelector(selectRevisionErrorById(revisionId));
  const saveStatus = useSelector(selectUpdateRevisionStatusById(revisionId));
  const saveError = useSelector(selectUpdateRevisionErrorById(revisionId));

  const [plannedFinishAt, setPlannedFinishAt] = useState(null);
  const [comment, setComment] = useState("");
  const [plannedFinishError, setPlannedFinishError] = useState(null);

  function startOfToday() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  }

  const fetchRevision = useCallback(async () => {
    dispatch(fetchInventoryRevisionDetail({ revisionId }));
  }, [dispatch, revisionId]);

  useEffect(() => {
    fetchRevision();
  }, [fetchRevision]);

  useEffect(() => {
    if (!revision) return;
    const d = revision?.planned_finish_at ? new Date(revision.planned_finish_at) : null;
    setPlannedFinishAt(d && !Number.isNaN(d.getTime()) ? d : null);
    setComment(String(revision?.comment || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision?.id]);

  const canSave = useMemo(() => saveStatus !== "saving", [saveStatus]);

  const onSave = useCallback(
    async (e) => {
      e.preventDefault();
      setPlannedFinishError(null);
      try {
        if (!plannedFinishAt || !(plannedFinishAt instanceof Date) || Number.isNaN(plannedFinishAt.getTime())) {
          setPlannedFinishError("Обязательное поле");
          return;
        }
        if (plannedFinishAt < startOfToday()) {
          setPlannedFinishError("Можно выбрать только сегодня и позже");
          return;
        }

        await dispatch(
          updateInventoryRevision({
            revisionId,
            planned_finish_at: plannedFinishAt.toISOString(),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/revision/${revisionId}`, { replace: true, state: { from: fromList } });
      } catch (err) {
        // error is in redux; keep local silent
      }
    },
    [comment, dispatch, fromList, navigate, plannedFinishAt, revisionId]
  );

  return (
    <div className="warehouse-revision-edit-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/revision"} className="link">
            Ревизии
          </Link>
          <span className="sep">/</span>
          <Link to={`/warehouse/revision/${revisionId}`} state={{ from: fromList }} className="link">
            Ревизия #{revisionId}
          </Link>
          <span className="sep">/</span>
          <span className="muted">Редактирование</span>
        </div>
        <div className="title">Редактировать ревизию #{revisionId}</div>
        {revision && (
          <div className="sub-title">
            Магазин: <b>{revision.store_name || revision.store || "—"}</b> · Статус:{" "}
            <b>{inventoryRevisionStatusLabel(revision.status)}</b>
          </div>
        )}
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {(loadError || saveError) && <div className="error">{loadError || saveError}</div>}

      {revision && (
        <form onSubmit={onSave} className="form">
          <label className="field">
            <div className="label">Время ревизии</div>
            <DatePicker
              selected={plannedFinishAt}
              onChange={(d) => setPlannedFinishAt(d)}
              showTimeSelect
              timeIntervals={15}
              timeFormat="HH:mm"
              dateFormat="dd.MM.yyyy HH:mm"
              minDate={startOfToday()}
              customInput={<input className="input" />}
            />
            {plannedFinishError && <div className="inline-error">{plannedFinishError}</div>}
            <div className="help">
              Выбрано: {plannedFinishAt ? plannedFinishAt.toLocaleString("ru-RU") : "—"} (можно только сегодня и позже)
            </div>
          </label>

          <label className="field">
            <div className="label">comment</div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Комментарий…"
              className="textarea"
              rows={4}
            />
          </label>

          <div className="actions">
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {saveStatus === "saving" ? "Сохраняю…" : "Сохранить"}
            </button>
            <Link to={`/warehouse/revision/${revisionId}`} state={{ from: fromList }} className="btn-ghost">
              Отмена
            </Link>
            <button type="button" onClick={fetchRevision} className="btn">
              Перезагрузить
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

